import { supabase } from './supabase'

type UsageEventType = 'module_view' | 'admin_view' | 'workspace_view'

type UsageSummary = {
  unique_today: number
  sessions_today: number
  unique_7: number
  sessions_7: number
  unique_15: number
  sessions_15: number
  unique_30: number
  sessions_30: number
  registered_active: number
  adoption_30: number
  last_event_at: string | null
  module_views: number
  admin_views: number
  workspace_entries: number
}

export type UsageIntelligence = {
  period_days: number
  summary: UsageSummary
  daily: Array<{ date: string; unique_users: number; sessions: number }>
  modules: Array<{ module_key: string; module_label: string; accesses: number; unique_users: number; last_access: string | null }>
  admin_areas: Array<{ module_key: string; module_label: string; accesses: number; unique_users: number; last_access: string | null }>
  users: Array<{
    user_id: string
    nome: string
    email: string
    role: string
    sessions: number
    active_days: number
    modules: number
    module_views: number
    admin_views: number
    activity_total: number
    last_access: string | null
  }>
  hourly: Array<{ hour: number; sessions: number }>
  inactivity: { never_accessed: number; inactive_15: number; inactive_30: number }
}

type StoredUsageSession = {
  userId: string
  sessionId: string
  createdAt: number
  lastSeenAt: number
}

const STORAGE_KEY = 'suite:usage-session'
const SESSION_IDLE_MS = 30 * 60 * 1000
const OPERATION_TIME_ZONE = 'America/Campo_Grande'
let memorySession: StoredUsageSession | null = null

function createSession(userId: string, now = Date.now()): StoredUsageSession {
  return {
    userId,
    sessionId: crypto.randomUUID(),
    createdAt: now,
    lastSeenAt: now,
  }
}

function operationDay(value: number) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: OPERATION_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(value))

  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

function sessionExpired(session: StoredUsageSession, now: number) {
  return now - session.lastSeenAt > SESSION_IDLE_MS || operationDay(session.createdAt) !== operationDay(now)
}

function persistSession(session: StoredUsageSession) {
  memorySession = session
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session)) } catch { /* sessão em memória permanece funcional */ }
}

function getStoredSession(userId: string) {
  const now = Date.now()

  if (memorySession?.userId === userId && !sessionExpired(memorySession, now)) {
    memorySession.lastSeenAt = now
    persistSession(memorySession)
    return memorySession.sessionId
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StoredUsageSession>
      if (
        parsed.userId === userId &&
        parsed.sessionId &&
        typeof parsed.createdAt === 'number' &&
        typeof parsed.lastSeenAt === 'number'
      ) {
        const restored = parsed as StoredUsageSession
        if (!sessionExpired(restored, now)) {
          restored.lastSeenAt = now
          persistSession(restored)
          return restored.sessionId
        }
      }
    }
  } catch {
    // Cria uma nova sessão abaixo.
  }

  const next = createSession(userId, now)
  persistSession(next)
  return next.sessionId
}

export function getUsageSessionId(userId: string) {
  return getStoredSession(userId)
}

export async function startUsageSession(userId: string) {
  if (!navigator.onLine) return null
  const sessionId = getStoredSession(userId)
  const { error } = await supabase.rpc('start_usage_session', { p_session_id: sessionId })
  if (error) throw error
  return sessionId
}

export async function recordUsageEvent(userId: string, eventType: UsageEventType, moduleKey: string, moduleLabel: string) {
  if (!navigator.onLine) return
  const sessionId = getStoredSession(userId)
  const { error } = await supabase.rpc('record_usage_event', {
    p_session_id: sessionId,
    p_event_type: eventType,
    p_module_key: moduleKey,
    p_module_label: moduleLabel,
  })
  if (error) throw error
}

export async function loadUsageIntelligence(days: number): Promise<UsageIntelligence> {
  const { data, error } = await supabase.rpc('usage_intelligence', { p_days: days })
  if (error) throw error
  return data as UsageIntelligence
}
